import { useEffect } from 'react';
import { css } from '@emotion/react';
// eslint-disable-next-line import/no-named-as-default
import ReCAPTCHA from 'react-google-recaptcha';

import { atoms, colors } from '@zapier/marketing-ui';
import { useTrackingContext } from '@zapier/toolbox-tracking';

import { dispatchSignupUserInteractionEvent } from '../../../avro';

export type CaptchaProps = {
  /**
   * Key used by Google reCaptcha
   */
  googleCaptchaSiteKey: string;
  /**
   * Callback on captcha expiration (the user did not complete it in the required amount of time)
   */
  onCaptchaExpiration?: () => void;
  /**
   * Callback for when the user successfully verifies they are not a robot
   */
  onCaptchaVerification: (captcha: string | null) => void;
};

const EVENT_CAPTCHA_RENDERED = 'signup: captcha rendered';
const EVENT_CAPTCHA_VERIFICATION_FAIL = 'signup: captcha verification failed';

const styles = {
  root: css`
    background: ${colors.neutral100};
  `,

  description: css`
    margin-bottom: 24px;
  `,

  recaptcha: css`
    display: flex;
    justify-content: center;
    margin-top: 30px;
  `,
};

/**
 * Shows a reCaptcha form to confirm the user isn't a robot
 */
export function Captcha(props: CaptchaProps) {
  const { trackEvent } = useTrackingContext();

  useEffect(() => {
    // Track that we showed the Captcha to the user
    trackEvent(EVENT_CAPTCHA_RENDERED);
    dispatchSignupUserInteractionEvent({
      signupFunnel: 'signup_captcha_rendered',
      // I don't think that SSO or SAML ever require captcha
      signupType: 'direct',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onExpired() {
    trackEvent(EVENT_CAPTCHA_VERIFICATION_FAIL, {
      reason: 'captcha expired',
    });
    dispatchSignupUserInteractionEvent({
      signupFunnel: 'signup_captcha_verification_failed',
      // I don't think that SSO or SAML ever require captcha
      signupType: 'direct',
    });
    if (props.onCaptchaExpiration) {
      props.onCaptchaExpiration();
    }
  }

  return (
    <section css={styles.root} data-testid="auth-captcha">
      <p
        className={`${atoms.body} ${atoms.textCenter}`}
        css={styles.description}
      >
        Looks like our robots want to make sure you&apos;re not one!
      </p>
      <ReCAPTCHA
        css={styles.recaptcha}
        sitekey={props.googleCaptchaSiteKey}
        onChange={props.onCaptchaVerification}
        onExpired={onExpired}
      />
    </section>
  );
}
